import React, { useCallback, useMemo, useRef, useState } from "react";
import { Drawer, Form, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useBoolean, useSafeState } from "ahooks";
import moment from "moment";
import { useSelector } from "react-redux";
import { PdFStepMapping } from "@containers/PdfReport";
import PdfForm from "@containers/PdfReport/PdfForm";
import { PdfProvider } from "@containers/PdfReport/context";
import useDateRange from "@/hooks/use-date-range";
import { safemomentmax, safemomentmin } from "@/containers/UtilityData/New-Summary/utils";
import { summaryChartClassnames } from "@/containers/UtilityData/New-Summary/components/summaryChartClassnames";
import { savingsChartClassnames } from "@/containers/UtilityData/New-Savings/components/savingsChartClassnames";
import ReportTable from "./ReportTable";
import ReportLoading from "./ReportLoading";
import ReportPreview from "./ReportPreview";

const getInitialValues = ({ facilityName, dateRange }) => {
  const elecMaxDate = moment(dateRange.date_range_elec_max);
  const gasMaxDate = moment(dateRange.date_range_gas_max);
  const elecMinDate = moment(dateRange.date_range_elec_min);
  const gasMinDate = moment(dateRange.date_range_gas_min);
  const maxDate = safemomentmax(elecMaxDate, gasMaxDate);
  const minDate = safemomentmin(elecMinDate, gasMinDate);
  return {
    name: `${facilityName} - FacilityKPI report ${moment().format("YYYY_MM_DD HH_mm")}`,
    utilityVisible: true,
    dashboardVisible: true,
    periodItem: summaryChartClassnames.map((item) => item.id),
    period: "12",
    endingMonth: maxDate.clone(),
    rangeMonth: [maxDate.clone().subtract(1, "year"), maxDate.clone()],
    allRangeMonth: [minDate.clone(), maxDate.clone()],
    savingAnalysisItem: savingsChartClassnames.map((item) => item.id),
    savingAnalysis: {
      elec_Option: "Baseline Average",
      gas_Option: "Baseline Average",
      elec_Baseline_Date: [
        elecMaxDate.clone().add(-23, "month").format("YYYY-MM"),
        elecMaxDate.clone().add(-12, "month").format("YYYY-MM"),
      ],
      elec_Post_Date: [
        elecMaxDate.clone().add(-11, "month").format("YYYY-MM"),
        elecMaxDate.clone().format("YYYY-MM"),
      ],
      gas_Baseline_Date: [
        gasMaxDate.clone().add(-23, "month").format("YYYY-MM"),
        gasMaxDate.clone().add(-12, "month").format("YYYY-MM"),
      ],
      gas_Post_Date: [
        gasMaxDate.clone().add(-11, "month").format("YYYY-MM"),
        gasMaxDate.clone().format("YYYY-MM"),
      ],
      gas_usedModuleValue: "Segments",
      elec_usedModuleValue: "Segments",
      gas_n_segments: 2,
      elec_n_segments: 2,
      breakpoints: [],
    },
  };
};

const Report = () => {
  const [isDrawerVisible, { setTrue: openDrawer, setFalse: closeDrawer }] = useBoolean(false);
  const [pdfFormValue, setPdfFormValue] = useSafeState({});
  const [previewUrl, setPreviewUrl] = useSafeState("");
  const [pdfStep, setPdfStep] = useState(PdFStepMapping.PDFForm);
  const [form] = Form.useForm();
  const tableActionRef = useRef();
  const facilityName = useSelector((state) => state?.Facility?.selected?.value ?? "");
  const dateRange = useDateRange();

  const reloadTable = useCallback(() => {
    if (tableActionRef.current && typeof tableActionRef.current.reload === "function") {
      tableActionRef.current.reload();
    }
  }, []);

  const initForm = useCallback(
    (values) => {
      form.setFieldsValue(values || getInitialValues({ facilityName, dateRange }));
    },
    [dateRange, facilityName, form]
  );

  const content = useMemo(() => {
    switch (pdfStep) {
      case PdFStepMapping.PDFLoading:
        return <ReportLoading />;
      case PdFStepMapping.PDFView:
        return <ReportPreview />;
      case PdFStepMapping.PDFForm:
      default:
        return <PdfForm />;
    }
  }, [pdfStep]);

  return (
    <PdfProvider
      value={{
        isDrawerVisible,
        openDrawer,
        closeDrawer,
        pdfFormValue,
        setPdfFormValue,
        pdfStep,
        goToFormStep: () => setPdfStep(PdFStepMapping.PDFForm),
        goToLoadingStep: () => setPdfStep(PdFStepMapping.PDFLoading),
        goToViewStep: () => setPdfStep(PdFStepMapping.PDFView),
        previewUrl,
        setPreviewUrl,
        reloadTable,
        tableActionRef,
        form,
        initForm,
      }}
    >
      <div style={{ padding: 10 }}>
        <Drawer
          title="Report Builder"
          extra={
            <Space>
              <CloseOutlined onClick={closeDrawer} />
            </Space>
          }
          open={isDrawerVisible}
          placement="right"
          width="100%"
          onClose={closeDrawer}
        >
          {content}
        </Drawer>
        <ReportTable />
      </div>
    </PdfProvider>
  );
};

export default React.memo(Report);
